import type { GqlSelectionListKey } from '../types';
import type { TaxonomyLevelMetadata } from './taxonomyTree';

/**
 * Backend geography level tag → the canonical FE selection-list key.
 *
 * This is the ONLY place the two vocabularies are reconciled. The backend
 * renamed `msa`→`metro_area` and `city`→`city_core` once already, and the
 * `?? msa` / `?? city` fallbacks that rename left scattered across geo
 * consumers all collapse into this table. Tags not listed here pass through
 * unchanged, so a new backend level reaches the UI with its backend label and
 * no FE edit.
 */
export const GEO_TAG_TO_SELECTION_LIST_KEY: Record<string, string> = {
  region: 'region',
  country: 'country',
  metro_area: 'msa',
  msa: 'msa',
  state: 'state',
  city_core: 'city',
  city: 'city',
  county: 'county',
};

const geoTagToSelectionListKey = (
  tag: string | null | undefined
): GqlSelectionListKey | undefined =>
  tag
    ? ((GEO_TAG_TO_SELECTION_LIST_KEY[tag] ?? tag) as GqlSelectionListKey)
    : undefined;

/**
 * Ordered selection-list keys for a geography taxonomy, shallowest first.
 * Level order comes from `levels[].id`, which is the depth its nodes nest at —
 * the filter engine derives a selection's `levelId` from its id depth, so this
 * array's ORDER is load-bearing, not just its contents.
 */
export const geoLevelsToSelectionListIds = (
  levels: readonly TaxonomyLevelMetadata[]
): GqlSelectionListKey[] =>
  [...levels]
    .sort((a, b) => a.id - b.id)
    .map((level) => geoTagToSelectionListKey(level.tag))
    .filter((key): key is GqlSelectionListKey => key !== undefined);
